import type { FC, ReactNode } from 'react'

import { useEffect, useRef } from 'react'
import { engine } from '@/lib/motion/engine'

const INTERACTIVE = 'a, button, [role="button"], input, select, textarea, label, summary, [data-cursor="hover"]'

const CustomCursor: FC = (): ReactNode => {
    const dotRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return

        const root = document.documentElement

        root.classList.add('cursor-custom')

        let x = -100
        let y = -100
        let scale = 1
        let targetScale = 1
        let visibility = 0
        let seeded = false

        const onOver = (event: PointerEvent): void => {
            const target = event.target as Element | null

            targetScale = target && typeof target.closest === 'function' && target.closest(INTERACTIVE) ? 2.4 : 1
        }

        document.addEventListener('pointerover', onOver)

        const unsubscribe = engine.subscribe((state) => {
            const dot = dotRef.current

            if (!dot) return

            if (!seeded && state.hasPointer) {
                x = state.pointerX
                y = state.pointerY
                seeded = true
            }

            const follow = 1 - Math.exp(-30 * state.dt)

            x += (state.pointerX - x) * follow
            y += (state.pointerY - y) * follow
            scale += (targetScale - scale) * (1 - Math.exp(-14 * state.dt))
            visibility += ((state.hasPointer ? 1 : 0) - visibility) * (1 - Math.exp(-10 * state.dt))

            dot.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) translate(-50%, -50%) scale(${scale.toFixed(3)})`
            dot.style.opacity = visibility.toFixed(3)
        }, 100)

        return () => {
            root.classList.remove('cursor-custom')
            document.removeEventListener('pointerover', onOver)
            unsubscribe()
        }
    }, [])

    return (
        <div
            ref={dotRef}
            aria-hidden='true'
            className='cursor-dot pointer-events-none fixed left-0 top-0 z-[200] size-2 rounded-full bg-white opacity-0 mix-blend-difference will-change-transform'
        />
    )
}

export default CustomCursor
